import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Loader2, Mail, Users, Clock, FolderInput } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bulk portal-invite modal.
 * Props:
 *   open: bool
 *   onClose: () => void
 *   accountType: 'client_portal' | 'employee_portal'
 *   batchId: optional bulk-upload batch id for "from_batch" scope
 *   onSent: () => void  // called after successful send (parent should refetch)
 */
export default function BulkPortalInviteModal({ open, onClose, accountType, batchId = null, onSent }) {
  const [counts, setCounts] = useState({ never_invited: 0, last_24h: 0, from_batch: 0 });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [scope, setScope] = useState("never_invited");

  const label = accountType === "client_portal" ? "client" : "employee";
  const labelPlural = accountType === "client_portal" ? "clients" : "employees";

  const fetchCounts = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ account_type: accountType });
      if (batchId) params.set("batch_id", batchId);
      const res = await axios.get(`${API}/portal/invitations/bulk-preview?${params.toString()}`);
      setCounts(res.data || { never_invited: 0, last_24h: 0, from_batch: 0 });
      // If batch given and has results, default scope
      if (batchId && (res.data?.from_batch || 0) > 0) {
        setScope("from_batch");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load invite counts");
    } finally {
      setLoading(false);
    }
  }, [open, accountType, batchId]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const currentCount = counts[scope] || 0;

  const handleSend = async () => {
    if (currentCount === 0) {
      toast.error("Nothing to send for this scope");
      return;
    }
    setSending(true);
    try {
      const body = { account_type: accountType, scope };
      if (scope === "from_batch") body.batch_id = batchId;
      const res = await axios.post(`${API}/portal/invitations/bulk-send`, body);
      const { created, skipped } = res.data || {};
      const skippedMsg = skipped ? `, ${skipped} skipped (already invited)` : "";
      toast.success(`Sent ${created} portal invite${created === 1 ? "" : "s"}${skippedMsg}`);
      onSent && onSent();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitations");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-md p-0 gap-0" data-testid="bulk-invite-modal">
        <DialogHeader className="p-6 pb-3">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5 text-primary" />
            Send portal invites
          </DialogTitle>
          <DialogDescription>
            Email portal access setup links to {labelPlural}.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <RadioGroup value={scope} onValueChange={setScope} className="space-y-2" data-testid="bulk-invite-scope">
              <label
                htmlFor="scope-never"
                className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition ${scope === "never_invited" ? "border-primary bg-primary/5" : "border-border"}`}
              >
                <RadioGroupItem value="never_invited" id="scope-never" className="mt-0.5" data-testid="scope-never-invited" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium text-sm">
                    <Users className="h-4 w-4" />
                    All {labelPlural} without portal access
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {counts.never_invited} {label}{counts.never_invited === 1 ? "" : "s"} eligible
                  </div>
                </div>
              </label>

              <label
                htmlFor="scope-24h"
                className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition ${scope === "last_24h" ? "border-primary bg-primary/5" : "border-border"}`}
              >
                <RadioGroupItem value="last_24h" id="scope-24h" className="mt-0.5" data-testid="scope-last-24h" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium text-sm">
                    <Clock className="h-4 w-4" />
                    Added in the last 24 hours
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {counts.last_24h} new {label}{counts.last_24h === 1 ? "" : "s"} eligible
                  </div>
                </div>
              </label>

              {batchId && (
                <label
                  htmlFor="scope-batch"
                  className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition ${scope === "from_batch" ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  <RadioGroupItem value="from_batch" id="scope-batch" className="mt-0.5" data-testid="scope-from-batch" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <FolderInput className="h-4 w-4" />
                      From the most recent bulk upload
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {counts.from_batch} {label}{counts.from_batch === 1 ? "" : "s"} from this batch
                    </div>
                  </div>
                </label>
              )}
            </RadioGroup>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/30 flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={onClose}
            disabled={sending}
            data-testid="bulk-invite-cancel"
          >
            Cancel
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={handleSend}
            disabled={sending || loading || currentCount === 0}
            data-testid="bulk-invite-send"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send {currentCount} invite{currentCount === 1 ? "" : "s"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
